import { apiService } from 'core/services/apiService';
import { fetchPreferenceChannels } from 'core/API/gameMasterSports';
import { fetchGracenoteSportsPreferences } from 'core/API/gameMasterSports';
import { fetchScreenGroups } from 'core/API/ScreenSetting';

/** Shared confirmation message for deactivation (used in Provider, Screen Group, Sports). */
export const DEACTIVATE_SCHEDULING_CONFIRM_MESSAGE =
    'This will deactivate Game Master scheduling. This action cannot be undone.';

/**
 * Call activate/deactivate scheduling API.
 * Does not modify any Game Master settings data.
 */
export const callActivateDeactivateScheduling = async (
    workplaceId: number,
    value: 0 | 1
): Promise<void> => {
    await apiService.post(
        `/game_master_settings/${workplaceId}/activate-deactive-scheduling/${value}`
    );
};

/**
 * Returns provider count (number of lineups with at least one saved channel).
 */
const getProviderCount = async (workplaceId: number): Promise<number> => {
    const channels = await fetchPreferenceChannels(workplaceId, 5000, false);
    const byLineup: Record<string, string[]> = {};
    channels.forEach((item: any) => {
        const key = String(item.lineup ?? item.lineupId ?? '').trim();
        if (!key) return;
        if (!byLineup[key]) byLineup[key] = [];
        const ch = item.channel ?? item.channelCode ?? '';
        if (String(ch).trim() !== '') byLineup[key].push(String(ch));
    });
    return Object.keys(byLineup).filter((k) => (byLineup[k]?.length ?? 0) > 0).length;
};

/**
 * Returns number of selected sports (preference sports).
 */
const getSportsCount = async (workplaceId: number): Promise<number> => {
    const prefs = await fetchGracenoteSportsPreferences(workplaceId);
    return prefs?.length ?? 0;
};

/**
 * Returns number of screen groups.
 */
const getScreenGroupsCount = async (workplaceId: number): Promise<number> => {
    const groups = await fetchScreenGroups(workplaceId);
    return groups?.length ?? 0;
};

/**
 * Returns whether scheduling should be activated based on latest data:
 * at least 1 provider, at least 1 screen group, at least 1 sport selected.
 */
export const getSchedulingShouldActivate = async (
    workplaceId: number
): Promise<boolean> => {
    try {
        const [providerCount, sportsCount, screenGroupsCount] = await Promise.all([
            getProviderCount(workplaceId),
            getSportsCount(workplaceId),
            getScreenGroupsCount(workplaceId),
        ]);
        return (
            providerCount >= 1 && sportsCount > 0 && screenGroupsCount >= 1
        );
    } catch (error) {
        console.error('Error in getSchedulingShouldActivate:', error);
        return false;
    }
};

/**
 * Activation conditions: at least 1 provider, sports selected, at least 1 screen group.
 * If conditions are met, calls activate-deactive-scheduling/1 and returns true; otherwise returns false.
 */
export const checkAndAutoActivateScheduling = async (
    workplaceId: number
): Promise<boolean> => {
    try {
        const shouldActivate = await getSchedulingShouldActivate(workplaceId);
        if (shouldActivate) {
            await callActivateDeactivateScheduling(workplaceId, 1);
            return true;
        }
        return false;
    } catch (error) {
        console.error('Error in checkAndAutoActivateScheduling:', error);
        return false;
    }
};
